import { existsSync, promises as fsp } from 'node:fs';
import path from 'node:path';
import { matchMonocerosFeature } from '../util/ref.js';
import { PLANS_DIR } from './claude-code-roles.js';
import type { CreateOptions } from './types.js';

/**
 * Map the friendly yml `permissionMode` option (on the claude-code feature)
 * to Claude Code's `settings.json` `permissions.defaultMode` value.
 *
 * Default is `auto` → Claude's Auto Mode: no per-action approval prompts and,
 * unlike Bypass, **no recurring "accept responsibility" warning** (a
 * background classifier vets actions). That is the comfortable "it just works"
 * default, well-suited to an isolated container. Alternatives:
 *   - `ask`    → `default`           (prompt as usual)
 *   - `edits`  → `acceptEdits`       (auto file edits, Bash still prompts)
 *   - `bypass` → `bypassPermissions` (no prompts at all; we suppress its
 *                                     one-time warning, see writeClaudePermissionMode)
 * Raw Claude values are accepted as-is too, so the escape hatch is always there.
 */
export function resolveClaudeDefaultMode(raw: string | undefined): string {
  switch ((raw ?? 'auto').trim()) {
    case 'ask':
    case 'default':
      return 'default';
    case 'edits':
    case 'acceptEdits':
      return 'acceptEdits';
    case 'plan':
      return 'plan';
    case 'bypass':
    case 'bypassPermissions':
      return 'bypassPermissions';
    case 'auto':
    case '':
      return 'auto';
    default:
      // Unknown value → the comfortable default rather than an error; a typo
      // should not strand the build.
      return 'auto';
  }
}

/**
 * Write Claude Code's default permission mode into the container's
 * `home/.claude/settings.json`, derived from the claude-code feature's
 * `permissionMode` option in the yml. No-op when the container has no
 * claude-code feature.
 *
 * This is written at **apply** (here in the scaffold), NOT baked into the
 * feature's cached image layer — so a change to the yml takes effect on the
 * next apply instead of being frozen by the layer cache (see ADR 0018). The
 * write **merges**: every other key the user (or Claude) put in settings.json
 * is preserved.
 *
 * Beyond `permissions.defaultMode` it manages two mode-specific keys so the
 * persona never faces a surprise prompt, switching them on/off to match the
 * mode (both are keys Claude itself writes, so this is the same class as our
 * trust pre-approval):
 *   - `auto`   → set `env.CLAUDE_CODE_ENABLE_AUTO_MODE=1` so Auto Mode is
 *                enabled where the account supports it.
 *   - `bypass` → set `skipDangerousModePermissionPrompt=true` so its one-time
 *                "accept responsibility" warning doesn't appear.
 */
export async function writeClaudePermissionMode(
  targetDir: string,
  features: CreateOptions['features'],
): Promise<void> {
  if (!features) return;
  const entry = Object.entries(features).find(
    ([ref]) => matchMonocerosFeature(ref)?.name === 'claude-code',
  );
  if (!entry) return; // no claude-code feature → nothing to configure
  const hasGraphify = Object.keys(features).some(
    (ref) => matchMonocerosFeature(ref)?.name === 'graphify',
  );
  const hasRoles = Object.keys(features).some(
    (ref) => matchMonocerosFeature(ref)?.name === 'claude-code-roles',
  );

  const raw = entry[1]?.permissionMode;
  const mode = resolveClaudeDefaultMode(
    typeof raw === 'string' ? raw : undefined,
  );

  const file = path.join(targetDir, 'home', '.claude', 'settings.json');
  await fsp.mkdir(path.dirname(file), { recursive: true });

  let config: Record<string, unknown> = {};
  if (existsSync(file)) {
    try {
      const txt = await fsp.readFile(file, 'utf8');
      if (txt.trim()) {
        const parsed: unknown = JSON.parse(txt);
        if (typeof parsed === 'object' && parsed !== null) {
          config = parsed as Record<string, unknown>;
        }
      }
    } catch {
      // Malformed settings.json — start from a clean object rather than
      // failing the apply; we only own the one key.
      config = {};
    }
  }

  const permissions =
    typeof config.permissions === 'object' && config.permissions !== null
      ? (config.permissions as Record<string, unknown>)
      : {};
  permissions.defaultMode = mode;
  applyPlansDirectory(permissions, hasRoles);
  config.permissions = permissions;

  // Auto Mode: enable it via env where the account supports it; otherwise drop
  // the flag so a switch away from auto doesn't leave it set. Preserve any
  // other env keys the user added.
  const env =
    typeof config.env === 'object' && config.env !== null
      ? (config.env as Record<string, unknown>)
      : {};
  if (mode === 'auto') {
    env.CLAUDE_CODE_ENABLE_AUTO_MODE = '1';
  } else {
    delete env.CLAUDE_CODE_ENABLE_AUTO_MODE;
  }
  if (Object.keys(env).length > 0) config.env = env;
  else delete config.env;

  // Bypass: pre-accept its one-time warning; otherwise clear the flag.
  if (mode === 'bypassPermissions') {
    config.skipDangerousModePermissionPrompt = true;
  } else {
    delete config.skipDangerousModePermissionPrompt;
  }

  applyGraphifyHooks(config, hasGraphify);

  await fsp.writeFile(file, `${JSON.stringify(config, null, 2)}\n`);
}

/**
 * Put the roles' plans directory on the session's list of allowed working
 * directories, so the skills can read it in every permission mode.
 *
 * Plans live under the persisted `~/.claude`, deliberately outside the
 * workspace, so a plan survives an apply. Outside is the problem: Claude Code
 * refuses to list or search a directory that is not a working directory of the
 * session, and refuses it *silently for a skill* - the `!`find …`` line in the
 * skill's preamble is not a prompt the user can approve, so the whole skill
 * aborts before it loads. Auto Mode hid this for months (its classifier lets
 * the read through); Claude Desktop attaches over SSH in `acceptEdits`, where
 * it fails every time.
 *
 * Only the plans directory, not `~/.claude`: this widens what a session may
 * read, and it should widen by exactly the directory the roles own.
 */
function applyPlansDirectory(
  permissions: Record<string, unknown>,
  hasRoles: boolean,
): void {
  const existing = Array.isArray(permissions.additionalDirectories)
    ? (permissions.additionalDirectories as unknown[]).filter(
        (entry) => typeof entry === 'string' && entry !== PLANS_DIR,
      )
    : [];
  const next = hasRoles ? [...existing, PLANS_DIR] : existing;
  if (next.length > 0) permissions.additionalDirectories = next;
  else delete permissions.additionalDirectories;
}

/** A Claude Code `PreToolUse` entry, as it sits in settings.json. */
interface PreToolUseEntry {
  matcher?: string;
  hooks?: Array<{ type?: string; command?: string }>;
}

/** Every hook command Monoceros owns here is recognised by this marker. */
const GRAPHIFY_HOOK_MARKER = 'graphify hook-guard';

/**
 * The two hooks that make the graph the agent's first stop, keyed on the tools
 * that read the codebase raw. Upstream's own wording, taken from the entries
 * `graphify install --project` writes.
 *
 * `Grep` is in the search matcher because Claude Code routes content search
 * through its dedicated Grep tool rather than through Bash, so a Bash-only
 * matcher never fires on the path an agent actually takes.
 */
function graphifyHookEntries(): PreToolUseEntry[] {
  return [
    {
      matcher: 'Bash|Grep',
      hooks: [{ type: 'command', command: `graphify hook-guard search` }],
    },
    {
      matcher: 'Read|Glob',
      hooks: [{ type: 'command', command: `graphify hook-guard read` }],
    },
  ];
}

/**
 * Add or remove graphify's `PreToolUse` hooks in the container's **global**
 * Claude settings, so a session in this workbench is nudged to ask the graph
 * before it reads or greps source files.
 *
 * Why we write them and upstream does not: `graphify install` only wires these
 * hooks in **project** scope, into a repo's own `.claude/settings.json`. That
 * would put Monoceros into the builder's repository and their git status, which
 * ADR 0049 ruled out - and the consequence was that a global install shipped
 * the `/graphify` trigger and no rule to use it at all. Three runs on the same
 * repository and the same graph took three different routes, one of them
 * answering entirely from the graph and another reading 926 lines of source,
 * because a briefing line is advice and nothing more.
 *
 * The hook command itself is scope-agnostic: `graphify hook-guard` keys off
 * `graphify-out/graph.json` in the working directory, prints nothing when there
 * is no graph, and always exits 0. So the container-global placement is safe
 * even in a workspace where no graph was ever built.
 *
 * `graphify` unqualified rather than an absolute path: the feature symlinks the
 * launcher into `/usr/local/bin`, which is on PATH for every shell type, and a
 * path into the uv tool venv would be a second thing to keep in step.
 *
 * Merging cuts both ways. Foreign `PreToolUse` entries are preserved, ours are
 * replaced rather than duplicated on re-apply, and removing the graphify
 * feature from the yml removes them again - otherwise every later session in
 * that container would run a hook command that is no longer installed.
 */
function applyGraphifyHooks(
  config: Record<string, unknown>,
  hasGraphify: boolean,
): void {
  const hooks =
    typeof config.hooks === 'object' && config.hooks !== null
      ? (config.hooks as Record<string, unknown>)
      : {};
  const existing = Array.isArray(hooks.PreToolUse)
    ? (hooks.PreToolUse as PreToolUseEntry[])
    : [];
  const foreign = existing.filter(
    (entry) =>
      !entry?.hooks?.some((h) => h?.command?.includes(GRAPHIFY_HOOK_MARKER)),
  );
  const next = hasGraphify ? [...foreign, ...graphifyHookEntries()] : foreign;

  if (next.length > 0) hooks.PreToolUse = next;
  else delete hooks.PreToolUse;

  if (Object.keys(hooks).length > 0) config.hooks = hooks;
  else delete config.hooks;
}
